import React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import PropTypes from 'prop-types';
import Loading from 'components/Loading';
import { Card, CollapseCard } from 'components/UI';
import { useSelector } from 'react-redux';
import {
  titleRowSx,
  listSx,
  listItemSx,
  listTextSx,
  collapseCardClassNames,
  collapseCardHostSx,
} from './InvoiceItems.styled';

const ListTitleComponent = ({ title, total }) => {
  return (
    <Grid container sx={titleRowSx}>
      <span style={{ flexGrow: 1, textAlign: 'left', marginRight: '5px' }}>{title}</span>
      <span style={{ flexGrow: 0 }} data-cy="auth-hold-title-description">
        {i18n.t('invoice.total_info', {
          total: formatAmount(total),
        })}
      </span>
    </Grid>
  );
};

const ItemComponent = ({ item }) => {
  const {
    description,
    unit_price,
    quantity,
    q_humanized,
    line_pre_tax: total,
    invoice_item_type: {
      name,
      description: itemDesc,
      units: { total: unit },
    },
  } = item;

  const invoiceItemInfo = [
    { label: i18n.t('common.description'), value: description || itemDesc || '--' },
    {
      label: i18n.t('invoice.unit_price'),
      value: unit_price != null ? `$${formatAmount(unit_price)}` : '--',
    },
    {
      label: i18n.t('invoice.quantity'),
      value:
        q_humanized !== undefined && quantity !== undefined ? `${q_humanized} (${quantity})` : '--',
    },
    { label: i18n.t('invoice.unit'), value: unit || '--' },
  ];

  return (
    <CollapseCard
      title={<ListTitleComponent data-cy="auth-hold-item-element" title={name} total={total} />}
      customClasses={collapseCardClassNames}
    >
      <List sx={listSx}>
        {invoiceItemInfo.map((info, index) => (
          <ListItem key={index} sx={listItemSx}>
            <ListItemText
              data-cy="auth-hold-item-data"
              primary={info.label}
              secondary={info.value}
              sx={listTextSx}
            />
          </ListItem>
        ))}
      </List>
    </CollapseCard>
  );
};

const AuthorizationHoldItems = () => {
  const { invoice, loading } = useSelector((state) => state.invoice);
  const items = invoice?.authorization_holds;

  return (
    <Card
      title={
        <Grid container alignItems="center" justifyContent="space-between">
          <span>{i18n.t('invoice.authorization_holds_title')}</span>
        </Grid>
      }
      headerActionType="vertical"
      datacy="authorization-holds-widget"
    >
      <Box>
        {loading ? (
          <Box>
            <Loading />
          </Box>
        ) : (
          <>
            {items?.length > 0 ? (
              <>
                <Box
                  sx={collapseCardHostSx}
                  style={{
                    height: '100%',
                    maxHeight: 'calc(100% - 52px)',
                    overflowY: 'auto',
                  }}
                >
                  {items.map((item, index) => (
                    <ItemComponent item={item} key={index} />
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="h4">
                <Box fontSize={14}>{i18n.t('common.empty_list')}</Box>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

ListTitleComponent.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
};

ItemComponent.propTypes = {
  item: PropTypes.object.isRequired,
};

export { AuthorizationHoldItems };
